import React, { useCallback, useEffect, useState } from 'react';

import isEmpty from 'lodash/isEmpty';
import isNull from 'lodash/isNull';
import isNumber from 'lodash/isNumber';
import toNumber from 'lodash/toNumber';

import { Flex } from '@qality/common-components/src/atoms/Flex';
import { Table } from '@qality/common-components/src/atoms/Table';
import { Text } from '@qality/common-components/src/atoms/Text';
import EmptyGadget from '@qality/common-components/src/gadgets/EmptyGadget';
import { SpinnerLoader } from '@qality/common-components/src/molecules/SpinnerLoader';
import { messages } from '@qality/common-messages';
import { Report, ReportFetchResult } from '@qality/common-model';
import { StorageOfUniqueServices } from '@qality/common-service';

import QalityPagination from '../QalityPagination';
import { TableWrapperStyled } from './ReportTable.styled';
import ReportTableRow from './ReportTableRow';
import { rows } from './ReportTableRows';
import './_report-row.scss';

type Props = {
  report: Report;
  providedReportData?: ReportFetchResult;
  noData: boolean;
  isLoading: boolean;
  setIsLoading: (value: boolean) => void;
};

function ReportTable({
  report,
  noData,
  providedReportData,
  isLoading,
  setIsLoading,
}: Props): JSX.Element {
  const [pagination, setPagination] = useState({});
  const [reportData, setReportData] = useState(null);
  const [projectId, setProjectId] = useState();
  const [currentFilters, setCurrentFilters] = useState({});

  const fetchReportData = useCallback(async () => {
    try {
      if (noData) {
        setIsLoading(false);
        return;
      }
      setIsLoading(true);
      const urlSearchParams = await StorageOfUniqueServices.get(
        'URLService'
      ).getURLSearchParams();

      const pageNumber = getPageNumber(urlSearchParams);
      const filters = await getFilters();
      const entries = Object.entries(filters);
      const data = await report.fetch(pageNumber, report.mapFilters(entries));
      setReportDataToState(data);
      setCurrentFilters(filters);
    } catch (e) {
      StorageOfUniqueServices.get('ErrorHandlingUtils').handleError(
        e,
        messages['error.report.loading.message']
      );
    }
  }, []);

  const setReportDataToState = (data: ReportFetchResult) => {
    setReportData(data.data);
    setPagination(data.pagination);
    setIsLoading(false);
  };

  useEffect(() => {
    if (providedReportData) {
      setReportDataToState(providedReportData);
    } else {
      fetchReportData();
    }
  }, []);

  const getFilters = async () => {
    const URLService = StorageOfUniqueServices.get('URLService');
    const urlSearchParams = await URLService.getURLSearchParams();
    const projectIdParam = urlSearchParams.get('projectId');

    const statuses = urlSearchParams.getAll('statuses').join(',');
    const filters = {};
    for (const [key, value] of urlSearchParams.entries()) {
      if (key !== 'pageNumber') {
        filters[key] = value;
      }
    }

    setProjectId(projectIdParam);
    filters['projectId'] = projectIdParam;
    filters['statuses'] = statuses;

    return report.retrieveFiltersWithDefaultValues(filters);
  };

  const handlePageChange = async (event: any, newPage: any) => {
    try {
      setIsLoading(true);
      const filters = await getFilters();
      const data = await report.fetch(
        newPage,
        report.mapFilters(Object.entries(filters))
      );
      StorageOfUniqueServices.get('URLService').updateUrlParams([
        { name: 'pageNumber', value: newPage },
      ]);

      setReportDataToState(data);
      setCurrentFilters(filters);
    } catch (e) {
      StorageOfUniqueServices.get('ErrorHandlingUtils').handleError(
        e,
        messages['error.report.loading.next.page.message']
      );
    }
  };

  if (isLoading) {
    return (
      <Flex justifyCenter paddingSize='medium'>
        <SpinnerLoader text='We are loading data...' />
      </Flex>
    );
  }

  let rowsData = [];
  if (reportData) {
    rowsData = rows(
      reportData.rows.content,
      report.getColumnNames(currentFilters),
      reportData.columnKeys
    );

    if (report.filters === null && rowsData.length === 0) {
      if (!projectId) {
        return <EmptyGadget message='There are no executed Test Cases yet' />;
      } else {
        return (
          <EmptyGadget message='There are no executed Test Cases in this project yet' />
        );
      }
    }
  }

  const relativeLinkIndex = report
    .getColumnNames(currentFilters)
    .findIndex((column) => column.type === 'RELATIVE_LINK');

  let handleClickRow;
  let extendProps = {};
  if (report.rowAction !== undefined) {
    handleClickRow = async (path) =>
      window.open(
        (await StorageOfUniqueServices.get('HostNameService').baseUrl) + path
      );
    extendProps = { pointer: true };
  }

  const cellPadding = report.styles?.cellPadding;

  return (
    <div>
      <TableWrapperStyled>
        <Table cellsSpacing={cellPadding}>
          <Table.Head>
            <Table.Tr>
              {report.getColumnNames(currentFilters).map((columnData, idx) => (
                <Table.Th
                  key={`${columnData.name}-${idx}`}
                  widths={{
                    width: columnData.width,
                    minWidth: columnData.minWidth,
                    maxWidth: columnData.maxWidth,
                  }}
                >
                  {columnData.name}
                </Table.Th>
              ))}
            </Table.Tr>
          </Table.Head>

          <Table.Body>
            {!rowsData.length && (
              <Table.Tr>
                <Table.Td colSpan={6}>
                  <Flex justifyCenter paddingSize='medium'>
                    <Text>There is no data to display</Text>
                  </Flex>
                </Table.Td>
              </Table.Tr>
            )}
            {rowsData.map((row, idx) => {
              const rowRawData = reportData.rows.content[idx];
              return (
                <ReportTableRow
                  key={row.key}
                  row={row}
                  extendProps={extendProps}
                  handleClickRow={handleClickRow}
                  rowRawData={rowRawData}
                  relativeLinkIndex={relativeLinkIndex}
                />
              );
            })}
          </Table.Body>
        </Table>
      </TableWrapperStyled>
      {!isNull(reportData) &&
      !isEmpty(reportData.rows.content) &&
      report.paginationDisabled !== true ? (
        <QalityPagination
          pagination={pagination}
          handleChange={handlePageChange}
        />
      ) : null}
    </div>
  );

  function getPageNumber(urlSearchParams) {
    let pageNumber;
    if (report.paginationDisabled === true) {
      pageNumber = 0;
    } else {
      pageNumber = isNumber(urlSearchParams.get('pageNumber'))
        ? urlSearchParams.get('pageNumber')
        : toNumber(urlSearchParams.get('pageNumber'));
    }
    return pageNumber;
  }
}

export default ReportTable;
